// src/components/RaceSessionSelect.jsx
import { useEffect, useMemo, useState } from "react";
import { fetchSheetViaProxy } from "../lib/sheets";
import CustomSelect from "./CustomSelect.jsx";

function toInt(v) {
    const n = parseInt(String(v ?? "").trim(), 10);
    return Number.isFinite(n) ? n : null;
}

const DEFAULT_SESSION_OPTIONS = [
    { value: "RACE", label: "RACE" },
    { value: "QUALY", label: "QUALY" },
    { value: "PRACTICE", label: "PRACTICE" },
];

const DEFAULT_CLASS_OPTIONS = [
    { value: "GESAMT", label: "Gesamt" },
    { value: "PRO", label: "PRO" },
    { value: "INT", label: "INT" },
    { value: "AM", label: "AM" },
];

export default function RaceSessionSelect({
                                              valueRace,
                                              onRaceChange,
                                              valueSession,
                                              onSessionChange,
                                              showSession = true,
                                              onTrackLabelChange,
                                              valueClass,
                                              onClassChange,
                                              showClass = false,
                                              onUserRaceChange,
                                              autoSelectFirst = true,

                                              sheetId,
                                              scheduleRange = "Schedule!A1:D",
                                              raceLabelPrefix = "R",

                                              // NEU: optional dynamisch
                                              sessionOptions,
                                              classOptions,
                                          }) {
    const [options, setOptions] = useState([]);

    const effectiveSessionOptions = useMemo(
        () =>
            Array.isArray(sessionOptions) && sessionOptions.length
                ? sessionOptions
                : DEFAULT_SESSION_OPTIONS,
        [sessionOptions]
    );

    const effectiveClassOptions = useMemo(
        () =>
            Array.isArray(classOptions) && classOptions.length
                ? classOptions
                : DEFAULT_CLASS_OPTIONS,
        [classOptions]
    );

    useEffect(() => {
        (async () => {
            try {
                const res = await fetchSheetViaProxy({
                    range: scheduleRange,
                    sheetId,
                });

                let matrix = [];
                if (Array.isArray(res?.values)) {
                    matrix = res.values;
                } else if (Array.isArray(res?.rows)) {
                    matrix = res.rows.map((r) => Object.values(r));
                }

                if (matrix.length && toInt(matrix[0]?.[0]) === null) {
                    matrix = matrix.slice(1);
                }

                const opts = matrix
                    .map((row) => {
                        const id = toInt(row?.[0]);
                        const track = String(row?.[1] ?? "").trim();
                        if (!id) return null;

                        return {
                            id,
                            track,
                            label: `${raceLabelPrefix}${id} – ${track}`,
                        };
                    })
                    .filter(Boolean)
                    .sort((a, b) => a.id - b.id);

                setOptions(opts);

                const current = opts.find((o) => o.id === valueRace) ?? opts[0];
                if (current) {
                    if (autoSelectFirst && typeof valueRace !== "number") {
                        onRaceChange?.(current.id);
                    }
                    onTrackLabelChange?.(current.label);
                }
            } catch (e) {
                console.warn("Schedule load failed", e);
                setOptions([]);
                onTrackLabelChange?.("");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sheetId, scheduleRange]);

    const handleRaceChange = (id) => {
        onUserRaceChange?.(id);
        onRaceChange?.(id);

        const o = options.find((x) => x.id === id);
        onTrackLabelChange?.(o ? o.label : "");
    };

    return (
        <div className="card rv-filter-card" style={{ display: "grid", gap: 8 }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <label className="rv-dd" style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                    <span className="rv-dd__label">Rennen</span>
                    <CustomSelect
                        value={valueRace ?? ""}
                        onChange={(value) => handleRaceChange(parseInt(value, 10))}
                        allowEmpty={false}
                        buttonClassName="rv-select--race"
                        options={options.map((o) => ({
                            value: o.id,
                            label: o.label,
                        }))}
                    />
                </label>

                {showSession && (
                    <label className="rv-dd" style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                        <span className="rv-dd__label">Session</span>
                        <CustomSelect
                            value={valueSession ?? effectiveSessionOptions[0]?.value ?? "RACE"}
                            onChange={(value) => onSessionChange?.(value)}
                            allowEmpty={false}
                            buttonClassName={`rv-select--${String(valueSession ?? "RACE").toLowerCase()}`}
                            options={effectiveSessionOptions}
                        />
                    </label>
                )}

                {showClass && effectiveClassOptions.length > 0 && (
                    <label className="rv-dd" style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                        <span className="rv-dd__label">Klasse</span>
                        <CustomSelect
                            value={valueClass ?? effectiveClassOptions[0]?.value ?? "GESAMT"}
                            onChange={(value) => onClassChange?.(value)}
                            allowEmpty={false}
                            buttonClassName={`rv-select--${String(valueClass ?? "GESAMT").toLowerCase()}`}
                            options={effectiveClassOptions}
                        />
                    </label>
                )}
            </div>
        </div>
    );
}
